/**
 * Presentation of syslog lines: severity labels, their colour, filters.
 *
 * Pure so the widths can be asserted. Severity labels share a column, and a
 * column of differently sized labels pushes every message to a different
 * start. Padding alone does not fix that -- see CLAUDE.md: without
 * `white-space: pre` the renderer collapses the padding away.
 */

const NAMES = ["EMERG", "ALERT", "CRIT", "ERR", "WARN", "NOTICE", "INFO", "DEBUG"] as const;

/** Wide enough for the longest name. */
export const SEVERITY_WIDTH = 6;

/**
 * Fixed-width label. A line that carried no priority shows "?" -- not INFO,
 * which is what a relay is told to assume and which would be a guess.
 */
export function severityLabel(severity: number | null): string {
  const name = severity === null ? "?" : (NAMES[severity] ?? "?");
  return name.padEnd(SEVERITY_WIDTH, " ");
}

/** Colour token for a severity; ordinary lines take the page's text colour. */
export function severityColor(severity: number | null): string {
  if (severity === null) return "var(--text-muted)";
  if (severity <= 3) return "var(--status-critical)";
  if (severity === 4) return "var(--status-warning)";
  if (severity === 7) return "var(--text-muted)";
  return "var(--text-secondary)";
}

export interface SeverityFilter {
  label: string;
  /** Lines at this severity or worse; null for all, including lines without one. */
  max: number | null;
}

export const SEVERITY_FILTERS: SeverityFilter[] = [
  { label: "ALL", max: null },
  { label: "WARN+", max: 4 },
  { label: "ERR+", max: 3 },
];

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Local time, to the second: when the line *arrived*, not what the device's clock said. */
export function lineTime(iso: string): string {
  return TIME_FORMAT.format(new Date(iso));
}
